import { useState } from 'react';

import { Toast, useToast } from '@zillow/constellation-10';

import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { useQueryClient } from '@zg-rentals/react-query5';
import { ProofRecomendedActions } from '@zg-rentals/renters-insurance-c10';
import { useRentalManagerRenterContactWebApiHandler_getTenants } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  emitProofOfInsuranceSendRequestEvent,
  PROOF_OF_INSURANCE_CS_TRIGGER_OBJECTS,
} from '../../../analytics/events/proofOfInsurance';

import { CS_EVENT_TRIGGER_LOCATION, emitTenantsModuleSaveClickEvent } from '../../../analytics/events/tenantDirectory';
import { createZgApiHttpClient } from '../../../clients/zgApiHttpClient';
import { useAuth } from '../../AuthProvider';

type Props = {
  listingAlias: string;
  onClose: () => void;
};

const ProofOfInsuranceModalWrapper = ({ listingAlias, onClose }: Props) => {
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [isModalTenantSelectOpen, setIsModalTenantSelectOpen] = useState(false);
  const [isProofRecomendedActionsOpen, setIsProofRecomendedActionsOpen] = useState(true);

  const client = createZgApiHttpClient(process.env.NEXT_PUBLIC_ZG_API_PUBLIC ?? '');

  const invalidateGetTenantsQuery = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerRenterContactWebApiHandler_getTenants.getKey({ listingAlias }),
    });
  };

  const handleSendRequestClick = () => {
    if (user) {
      emitProofOfInsuranceSendRequestEvent({
        user,
        currentProperty: { listingAlias },
        triggerLocation: PROOF_OF_INSURANCE_CS_TRIGGER_OBJECTS.RECOMMENDED_ACTIONS,
      });
    }
  };

  const handleAddTenantsClick = () => {
    setIsModalTenantSelectOpen(true);
    setIsProofRecomendedActionsOpen(false);
  };

  const handleOnModalClose = () => {
    setIsModalTenantSelectOpen(false);
    setIsProofRecomendedActionsOpen(false);
    onClose();
  };

  const handleSaveTenantsClick = () => {
    invalidateGetTenantsQuery();
    setIsModalTenantSelectOpen(false);
    setIsProofRecomendedActionsOpen(true);
  };

  const handleRequestProofSuccess = (message: string) => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="success">{message}</Toast>);
  };

  const handleRequestProofError = (message: string) => {
    invalidateGetTenantsQuery();
    enqueueToast(<Toast tone="critical">{message}</Toast>);
  };

  return (
    <>
      <ProofRecomendedActions
        listingAlias={listingAlias}
        isOpen={isProofRecomendedActionsOpen}
        onSendRequestClick={handleSendRequestClick}
        onAddTenantsClick={handleAddTenantsClick}
        onSendRequestEmailSuccess={handleRequestProofSuccess}
        onSendRequestEmailError={handleRequestProofError}
        onClose={handleOnModalClose}
      />
      <ModalTenantSelect
        client={client}
        listingAlias={listingAlias}
        isOpen={isModalTenantSelectOpen}
        onAddRenterSuccess={() => {
          if (user) {
            emitTenantsModuleSaveClickEvent({
              triggerLocation: CS_EVENT_TRIGGER_LOCATION.ZRM_POI_ADD_TENANTS_MODAL,
              currentProperty: { listingAlias },
              user,
            });
          }
        }}
        onAddRenterError={handleOnModalClose}
        queryClient={queryClient}
        onClose={handleOnModalClose}
        onPrimaryCTAClick={handleSaveTenantsClick}
        closeOnSuccess={false}
      />
    </>
  );
};

export default ProofOfInsuranceModalWrapper;
